import {
  buildUnderlineRoleMap,
  computeUnderlineCh,
  MdsSummaryTable
} from '@opengov/reporting-embeds/src/tables/components/Table';
import { createAccountSummaryFormatter } from '@opengov/reporting-embeds/src/tables/formatters';
import {
  EColumnType,
  ETableContentType,
  ISummaryTableColumn,
  ISummaryTableRow
} from '@opengov/reporting-embeds/src/tables/types';
import {
  accountSummaryFormatters,
  EN_DASH,
  prefixCurrencySymbol
} from '@opengov/reporting-formatters/formatters/accountSummaryFormatters';
import { IAccountSummaryFormatting } from '@opengov/reporting-types';
import React from 'react';

import { ECurrencySymbolDisplay, EFontSize } from '../types';
import { divideTablePerColumns } from './contentFormatter';

interface ExportPdfEmbedTableProps {
  embed: any;
  columnsPerPage: number;
  nameWidth: number;
  fontSize: EFontSize;
  showGridlines: boolean;
  showUnderlines?: boolean;
  currencySymbolDisplay: ECurrencySymbolDisplay;
  keepRowGroupsTogetherOnPageBreaks: boolean;
  keepColumnGroupsTogetherOnPageBreaks: boolean;
}

const fontMap = {
  [EFontSize.extraSmall]: 6,
  [EFontSize.small]: 8,
  [EFontSize.normal]: 10,
  [EFontSize.large]: 12,
  [EFontSize.extraLarge]: 14
};

type FormatterSet = ReturnType<typeof accountSummaryFormatters>;

const formatCurrencyCell = (value: string, formatter: FormatterSet, applyPrefix: boolean): string => {
  const formatted = formatter.currency(value);
  if (applyPrefix && (formatted === EN_DASH || formatted.trim() === '')) {
    return prefixCurrencySymbol(formatted);
  }
  return formatted;
};

const formatAllCurrencyCells = (rows: ISummaryTableRow[], formatter: FormatterSet): ISummaryTableRow[] => {
  return rows.map((row) => ({
    ...row,
    col: row.col?.map((cell) => {
      if (cell.format !== EColumnType.Currency) return cell;
      return { ...cell, value: formatter.currency(cell.value ?? ''), format: undefined };
    }),
    rows: row.rows?.length ? formatAllCurrencyCells(row.rows, formatter) : row.rows
  }));
};

const findLastGroupTotalIndex = (rows: ISummaryTableRow[]): number => {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].type === ETableContentType.RowGroupTotal) return i;
  }
  return -1;
};

export const formatRowGroup = (
  group: ISummaryTableRow,
  withSymbol: FormatterSet,
  withoutSymbol: FormatterSet
): ISummaryTableRow => {
  if (!group.rows?.length) return group;

  const lastGroupTotalIndex = findLastGroupTotalIndex(group.rows);

  const formattedChildren = group.rows.map((child, index) => {
    const isFirstChild = index === 0;
    const isTopLevelGroupTotal = index === lastGroupTotalIndex;
    const showDollar = isFirstChild || isTopLevelGroupTotal;
    const formatter = showDollar ? withSymbol : withoutSymbol;

    const formattedCol = child.col?.map((cell) => {
      if (cell.format !== EColumnType.Currency) return cell;
      return { ...cell, value: formatCurrencyCell(cell.value ?? '', formatter, showDollar), format: undefined };
    });

    const formattedNestedRows = child.rows?.length ? formatAllCurrencyCells(child.rows, withoutSymbol) : child.rows;

    return { ...child, col: formattedCol, rows: formattedNestedRows };
  });

  return { ...group, rows: formattedChildren };
};

export const applyFirstLastGroupFormatting = (
  data: ISummaryTableRow[],
  formatting: IAccountSummaryFormatting
): ISummaryTableRow[] => {
  const [headers, subHeaders, ...bodyGroups] = data;
  const withSymbol = accountSummaryFormatters({ ...formatting, showCurrencySymbol: true });
  const withoutSymbol = accountSummaryFormatters({ ...formatting, showCurrencySymbol: false });

  const formattedGroups = bodyGroups.map((group) => formatRowGroup(group, withSymbol, withoutSymbol));
  return [headers, subHeaders, ...formattedGroups];
};

const getFormattingOverride = (
  formatting: IAccountSummaryFormatting,
  currencySymbolDisplay: ECurrencySymbolDisplay
): IAccountSummaryFormatting => {
  if (currencySymbolDisplay === ECurrencySymbolDisplay.None) {
    return { ...formatting, showCurrencySymbol: false };
  }
  return { ...formatting, showCurrencySymbol: true };
};

export const ExportPdfEmbedTable: React.FC<ExportPdfEmbedTableProps> = ({
  embed,
  columnsPerPage,
  nameWidth,
  fontSize,
  showGridlines,
  showUnderlines = false,
  currencySymbolDisplay,
  keepRowGroupsTogetherOnPageBreaks,
  keepColumnGroupsTogetherOnPageBreaks
}) => {
  const tableData = JSON.parse(JSON.stringify(embed?.data?.sdk?.componentProps?.data)) as
    | { content?: ISummaryTableRow[]; columns?: ISummaryTableColumn[] }
    | undefined;
  const formatting = embed?.data?.sdk?.componentProps?.formatting as IAccountSummaryFormatting;
  const embedId = embed?.id as string;

  const isFirstLastPerGroup = currencySymbolDisplay === ECurrencySymbolDisplay.FirstLastPerGroup;
  const processedContent: ISummaryTableRow[] | undefined =
    isFirstLastPerGroup && tableData?.content
      ? applyFirstLastGroupFormatting(tableData.content, formatting)
      : tableData?.content;

  const tableArray = processedContent
    ? divideTablePerColumns(processedContent, 0, columnsPerPage, {
        keepColumnGroupsTogether: keepColumnGroupsTogetherOnPageBreaks
      })
    : [];
  const formattingOverride = getFormattingOverride(formatting, currencySymbolDisplay);

  // Compute underline width once across the full pre-split body so every chunk renders
  // at the same width regardless of per-period value lengths.
  const globalUnderlineWidth = (() => {
    if (!showUnderlines || !processedContent) return 0;
    const fullBodyRows = processedContent.slice(2);
    const fullUnderlineRoleMap = buildUnderlineRoleMap(fullBodyRows);
    return computeUnderlineCh(fullBodyRows, fullUnderlineRoleMap, createAccountSummaryFormatter(formattingOverride));
  })();

  if (!embed) return null;

  return (
    <>
      {tableArray.map((partialData, index) => (
        <div key={index} style={{ paddingBottom: '32px' }}>
          <MdsSummaryTable
            columns={tableData?.columns ?? []}
            data={partialData}
            embedId={embedId}
            accountSummaryTableFormatting={formattingOverride}
            printStyle={{
              nameWidth,
              fontSize: fontMap[fontSize],
              showGridlines,
              showUnderlines,
              keepRowGroupsTogetherOnPageBreaks,
              keepColumnGroupsTogetherOnPageBreaks,
              underlineWidth: globalUnderlineWidth
            }}
          />
        </div>
      ))}
    </>
  );
};
